import { useEffect, useMemo, useState } from 'react';
import { BottomSheet } from '../premium';
import { ChipGrid, PickerChip, SectionLabel } from '../picker/PickerPrimitives';
import { fetchExerciseLibrary } from '../ExerciseNameAutocomplete';
import {
  CATEGORY_ORDER,
  COMMON_LIFT_NAMES,
  LIFT_AUTO,
  categorizeLift,
  fuzzyMatchNames,
  liftFromName,
  loadRecentLifts,
  pushRecentLift,
  type LiftDef,
} from '../../lib/liftPicker';

export function LiftPickerSheet({
  open,
  onClose,
  onSelect,
  selectedId,
  userId,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (lift: LiftDef) => void;
  selectedId?: string | null;
  userId?: string;
}) {
  const [query, setQuery] = useState('');
  const [builtIn, setBuiltIn] = useState<string[]>([]);
  const [custom, setCustom] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const trimmed = query.trim();
  const searchActive = trimmed.length > 0;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    void fetchExerciseLibrary()
      .then((lib) => {
        if (cancelled) return;
        setBuiltIn(lib.builtIn ?? []);
        setCustom(lib.custom ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setBuiltIn([]);
          setCustom([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const allNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of [...builtIn, ...custom, ...COMMON_LIFT_NAMES]) {
      const t = n.trim();
      if (t.length < 2) continue;
      map.set(t.toLowerCase(), t);
    }
    return [...map.values()].sort((a, b) => a.localeCompare(b, 'en'));
  }, [builtIn, custom]);

  const recent = useMemo(() => {
    if (searchActive) return [];
    return loadRecentLifts(userId)
      .map((name) => {
        const hit = allNames.find((n) => n.toLowerCase() === name.toLowerCase());
        return liftFromName(hit ?? name);
      })
      .slice(0, 8);
  }, [searchActive, userId, open, allNames]);

  const commonVisible = useMemo(() => {
    if (searchActive) return [];
    return COMMON_LIFT_NAMES.map((name) => {
      const hit = allNames.find((n) => n.toLowerCase() === name.toLowerCase());
      return hit ? liftFromName(hit) : liftFromName(name);
    });
  }, [allNames, searchActive]);

  const visibleNames = useMemo(() => {
    if (searchActive) return fuzzyMatchNames(trimmed, allNames).slice(0, 48);
    return allNames;
  }, [searchActive, trimmed, allNames]);

  const grouped = useMemo(() => {
    const skip = new Set<string>();
    if (!searchActive) {
      for (const r of recent) skip.add(r.label.toLowerCase());
      for (const c of commonVisible) skip.add(c.label.toLowerCase());
    }
    const map = new Map<string, LiftDef[]>();
    for (const name of visibleNames) {
      if (!searchActive && skip.has(name.toLowerCase())) continue;
      const lift = liftFromName(name);
      const cat = categorizeLift(name);
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(lift);
    }
    return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => [c, map.get(c)!] as const);
  }, [visibleNames, searchActive, recent, commonVisible]);

  function pick(lift: LiftDef) {
    pushRecentLift(userId, lift.label);
    onSelect(lift);
    setQuery('');
    onClose();
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="Choose exercise">
      <p className="-mt-2 mb-3 text-sm text-[var(--text-muted)]">
        Pick any exercise from your library for AI form analysis.
      </p>

      <div className="sticky top-0 z-20 -mx-5 -mt-1 border-b border-white/[0.06] bg-[var(--surface-elevated)] px-5 pb-4">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search exercises"
          className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] focus:border-[#4F8CFF]/40 focus:shadow-[0_0_0_3px_rgba(79,140,255,0.12)]"
        />
      </div>

      <div className="space-y-5 pt-4">
        <div>
          <SectionLabel>Auto</SectionLabel>
          <ChipGrid>
            <PickerChip
              name="Auto detect"
              variant="auto"
              selected={selectedId === 'auto'}
              onClick={() => pick(LIFT_AUTO)}
            />
          </ChipGrid>
          <p className="mt-2 text-[11px] text-[var(--text-muted)]">
            We&apos;ll try to identify the movement from the video.
          </p>
        </div>

        {loading && allNames.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">Loading exercise library…</p>
        ) : null}

        {recent.length > 0 && !searchActive ? (
          <div>
            <SectionLabel>Recent</SectionLabel>
            <ChipGrid>
              {recent.map((lift) => (
                <PickerChip
                  key={lift.id}
                  name={lift.label}
                  variant="recent"
                  selected={selectedId === lift.id}
                  onClick={() => pick(lift)}
                />
              ))}
            </ChipGrid>
          </div>
        ) : null}

        {!searchActive ? (
          <div>
            <SectionLabel>Common</SectionLabel>
            <ChipGrid>
              {commonVisible.map((lift) => (
                <PickerChip
                  key={lift.id}
                  name={lift.label}
                  variant="neutral"
                  selected={selectedId === lift.id}
                  onClick={() => pick(lift)}
                />
              ))}
            </ChipGrid>
          </div>
        ) : null}

        {searchActive && visibleNames.length === 0 ? (
          <div>
            <p className="mb-3 text-sm text-[var(--text-muted)]">No matches for &ldquo;{trimmed}&rdquo;</p>
            {trimmed.length >= 2 ? (
              <ChipGrid>
                <PickerChip
                  name={`+ Use “${trimmed}”`}
                  variant="create"
                  onClick={() => pick(liftFromName(trimmed))}
                />
              </ChipGrid>
            ) : null}
          </div>
        ) : searchActive ? (
          <div>
            <SectionLabel>Results</SectionLabel>
            <ChipGrid>
              {visibleNames.map((name) => {
                const lift = liftFromName(name);
                return (
                  <PickerChip
                    key={lift.id}
                    name={lift.label}
                    variant="neutral"
                    selected={selectedId === lift.id}
                    onClick={() => pick(lift)}
                  />
                );
              })}
            </ChipGrid>
          </div>
        ) : (
          grouped.map(([cat, lifts]) => (
            <div key={cat}>
              <SectionLabel>{cat}</SectionLabel>
              <ChipGrid>
                {lifts.map((lift) => (
                  <PickerChip
                    key={lift.id}
                    name={lift.label}
                    variant="neutral"
                    selected={selectedId === lift.id}
                    onClick={() => pick(lift)}
                  />
                ))}
              </ChipGrid>
            </div>
          ))
        )}
      </div>
    </BottomSheet>
  );
}
